import { useEffect, useRef } from "react"
import { useSelector } from "react-redux"
import { isAdminPortalUser } from "../helpers/adminRoles"
import {
  ACTIVE_SESSION_UNLOAD_MESSAGE,
  clearActiveSessionSnapshot,
  fetchActiveSession,
  getActiveSessionSnapshot,
  resolveActiveSessionOpenPayload,
  resolveShouldPause,
  sendUnloadPause,
  syncActiveSessionSnapshot,
} from "../helpers/activeSession"
import { TIMELOG_REASONS } from "../helpers/sessionDetail"

/**
 * Fetches GET /sessions/active once per authenticated member token and opens
 * the global minimized player when a live session exists. Also attaches
 * native unload warning + keepalive timer pause (break only, no leave).
 * Skipped for admin / city-admin portal users.
 */
const useActiveSessionLifecycle = ({ openSessionDetail, close, player }) => {
  const isAuth = useSelector(state => state.auth.isAuth)
  const token = useSelector(state => state.auth.jid)
  const currentUser = useSelector(state => state.auth.currentUser)
  const userId = currentUser?.id
  const isAdminPortal = isAdminPortalUser(currentUser)
  const fetchedForRef = useRef(null)
  const playerRef = useRef(player)
  playerRef.current = player

  useEffect(() => {
    syncActiveSessionSnapshot({
      ...getActiveSessionSnapshot(),
      token: token || null,
    })
  }, [token])

  useEffect(() => {
    if (!isAuth || !token) {
      fetchedForRef.current = null
      clearActiveSessionSnapshot()
      close()
      return undefined
    }

    // Wait for /me (or cached user) so we know portal type before calling.
    if (!currentUser) return undefined

    if (isAdminPortal) {
      fetchedForRef.current = null
      return undefined
    }

    if (fetchedForRef.current === token) return undefined

    let cancelled = false
    let completed = false
    fetchedForRef.current = token

    const loadActiveSession = async () => {
      const result = await fetchActiveSession()
      completed = true
      if (cancelled) return
      if (!result.ok || !result.session) return

      const payload = resolveActiveSessionOpenPayload(result.session)
      if (!payload) return

      const currentId = playerRef.current?.payload?.sessionId
      const alreadyOpen = currentId && String(currentId) === payload.sessionId

      syncActiveSessionSnapshot({
        sessionId: payload.sessionId,
        token,
        shouldPause: resolveShouldPause(result.session, userId),
      })

      openSessionDetail({
        ...payload,
        mode: alreadyOpen ? undefined : "minimized",
      })
    }

    loadActiveSession()

    return () => {
      cancelled = true
      if (!completed && fetchedForRef.current === token) {
        fetchedForRef.current = null
      }
    }
  }, [
    isAuth,
    token,
    userId,
    currentUser,
    isAdminPortal,
    close,
    openSessionDetail,
  ])

  useEffect(() => {
    if (isAdminPortal) return undefined

    const onBeforeUnload = event => {
      const snap = getActiveSessionSnapshot()
      if (!snap.sessionId || !snap.shouldPause) return undefined
      event.preventDefault()
      event.returnValue = ACTIVE_SESSION_UNLOAD_MESSAGE
      return ACTIVE_SESSION_UNLOAD_MESSAGE
    }

    const onPageHide = event => {
      if (event.persisted) return
      sendUnloadPause(TIMELOG_REASONS.unload)
    }

    window.addEventListener("beforeunload", onBeforeUnload)
    window.addEventListener("pagehide", onPageHide)
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload)
      window.removeEventListener("pagehide", onPageHide)
    }
  }, [isAdminPortal])
}

export default useActiveSessionLifecycle
